import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ContactSection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: '',
    company: '',
    message: ''
  });
  const [status, setStatus] = useState(null); // 'success', 'error', 'loading'

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('loading');
    
    // Simulate API call
    setTimeout(() => {
      setStatus('success');
      setFormData({ name: '', lastName: '', email: '', company: '', message: '' });
      setTimeout(() => setStatus(null), 5000);
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contacto" className="py-12 bg-white border-t border-gray-100 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-brand-electric-blue/5 blur-[120px] rounded-full -mr-24 -mt-24"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-brand-neon-green/5 blur-[100px] rounded-full -ml-20 -mb-20"></div>

      <div className="container mx-auto px-6 max-w-5xl relative z-10">
        <div className="text-center mb-10 animate-on-scroll">
          <h2 className="font-audiowide text-3xl md:text-4xl text-[#111111] mb-6">
            {t('contact.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-brand-electric-blue to-brand-neon-green mx-auto rounded-full"></div>
          {t('contact.subtitle') && (
            <p className="font-montserrat text-gray-500 max-w-2xl mx-auto text-lg leading-relaxed mt-6">
              {t('contact.subtitle')}
            </p>
          )}
        </div>

        <div className="bg-white rounded-3xl shadow-xl shadow-brand-electric-blue/5 border border-gray-200/50 overflow-hidden flex flex-col md:flex-row relative z-10">
          {/* Info Side */}
          <div className="bg-gradient-to-br from-brand-electric-blue to-[#0052D4] p-8 md:w-2/5 text-white flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 blur-3xl rounded-full -mr-32 -mt-32"></div>
            <div className="relative z-10">
              <h3 className="font-audiowide text-xl mb-8 text-white">Información</h3>
              <div className="space-y-6 font-montserrat">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0 border border-white/20">
                    <i className="fas fa-envelope text-white"></i>
                  </div>
                  <div>
                    <p className="text-white/60 text-[10px] uppercase tracking-widest mb-1">Email</p>
                    <p className="font-semibold text-white">info@4tlayer.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0 border border-white/20">
                    <i className="fas fa-map-marker-alt text-white"></i>
                  </div>
                  <div>
                    <p className="text-white/60 text-[10px] uppercase tracking-widest mb-1">Oficinas</p>
                    <p className="font-semibold text-white">Madrid, España / Latam</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative z-10 pt-10 border-t border-white/10 mt-10">
              <p className="text-white/40 text-[10px] font-audiowide tracking-widest uppercase">
                4tlayer Infrastructure
              </p>
            </div>
          </div>

          {/* Form Side */}
          <div className="p-8 md:w-3/5">
            <form onSubmit={handleSubmit} className="space-y-4 font-montserrat">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">{t('contact.name')}</label>
                  <input
                    required
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-5 py-3 rounded-xl bg-gray-50/50 border border-gray-200 focus:border-brand-electric-blue focus:bg-white focus:ring-4 focus:ring-brand-electric-blue/5 outline-none transition-all placeholder:text-gray-300 text-sm"
                    placeholder="Juan"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">{t('contact.lastName')}</label>
                  <input
                    required
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-5 py-3 rounded-xl bg-gray-50/50 border border-gray-200 focus:border-brand-electric-blue focus:bg-white focus:ring-4 focus:ring-brand-electric-blue/5 outline-none transition-all placeholder:text-gray-300 text-sm"
                    placeholder="Pérez"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">{t('contact.email')}</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-5 py-3 rounded-xl bg-gray-50/50 border border-gray-200 focus:border-brand-electric-blue focus:bg-white focus:ring-4 focus:ring-brand-electric-blue/5 outline-none transition-all placeholder:text-gray-300 text-sm"
                    placeholder="info@4tlayer.com"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">{t('contact.company')}</label>
                  <input
                    required
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-5 py-3 rounded-xl bg-gray-50/50 border border-gray-200 focus:border-brand-electric-blue focus:bg-white focus:ring-4 focus:ring-brand-electric-blue/5 outline-none transition-all placeholder:text-gray-300 text-sm"
                    placeholder="4tlayer"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">{t('contact.message')}</label>
                <textarea
                  required
                  rows="2"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-5 py-3 rounded-xl bg-gray-50/50 border border-gray-200 focus:border-brand-electric-blue focus:bg-white focus:ring-4 focus:ring-brand-electric-blue/5 outline-none transition-all resize-none placeholder:text-gray-300 text-sm"
                  placeholder="..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className={`w-full py-4 rounded-xl bg-gradient-to-r from-brand-electric-blue to-brand-neon-green text-white font-bold text-base transition-all duration-300 flex items-center justify-center gap-3 shadow-lg shadow-brand-neon-green/20 hover:shadow-brand-neon-green/40 hover:-translate-y-1 ${status === 'loading' ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {status === 'loading' ? (
                  <i className="fas fa-circle-notch animate-spin"></i>
                ) : (
                  <i className="fas fa-paper-plane text-xs"></i>
                )}
                {t('contact.send')}
              </button>

              {status === 'success' && (
                <div className="p-4 rounded-xl bg-green-50 text-green-700 border border-green-100 text-center font-bold text-sm animate-bounce">
                  {t('contact.success')}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
